import React, { Dispatch, useCallback, useState } from "react"
import { Box } from "@mui/material"
import { Alert, Button, Grid, Icon, Modal, ModalTitle, ToastController, Typography } from "@platform-ui/design-system"
import { ExistingDestination, PreparedDestination, useCreateDestination, useTestConnection } from "@prequel/react";
import Connect from "../../../../Connect/Connect";
import { SubmitHandler } from "react-hook-form";
import { Action, useStoreContext } from "../../../../Store";
import { IADState } from "../../../../IntegrationApps/IntegrationAppDetails/state";
import { AUTH_TOKEN_URL, PREQUEL_API_HOST, VENDOR_TO_CONNECTOR_TITLE_MAPPING, ZDQ_VENDOR_CONNECTOR_TYPES } from "../Constants";
import { ZDQ_DESTINATION_DATA } from "../action_types";

type TestConnectionProps = {
    handleSubmit: any;
    vendor: ZDQ_VENDOR_CONNECTOR_TYPES;
    preparedDestination: PreparedDestination;
    fetchTokenWithDestination: (url: string, preparedDestination: PreparedDestination) => Promise<string>;
    setMainModalOpen: (value: boolean) => void;
    getPreparedDestinationWithFormValues: () => PreparedDestination;
    isEditMode?: boolean;
    existingDestinationData?: ExistingDestination;
};

function TestConnection({ handleSubmit, vendor, preparedDestination, fetchTokenWithDestination, setMainModalOpen, getPreparedDestinationWithFormValues, isEditMode = false, existingDestinationData }: TestConnectionProps) {
    const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
    const [testConnectionPassed, setTestConnectionPassed] = useState(false);
    const [toast, setToast] = useState({
        show: false,
        msg: '',
        severity: '',
        duration: 0,
        loading: false
    });
    const { state, dispatch } = useStoreContext() as { state: IADState, dispatch: Dispatch<Action> };
    const fetchAuthTokenUrl = `${state.settingsUrl.replace('.json', AUTH_TOKEN_URL)}`;

    const sendSlackNotification = async (payload = {}) => {
        try {
            const csrf = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content');
            const options = {
                method: 'POST',
                headers: new Headers({
                    'Accept': 'application/json',
                    'Content-type': 'application/json',
                    'X-CSRF-Token': csrf
                }),
                body: JSON.stringify(payload)
            };

            const response = await window.fetch(`${state.settingsUrl.replace('.json', '/zdq/notification')}`, options);
            if (!response.ok) throw Error(response.statusText);
            const jsonResponse = await response.json();
            return;

        } catch (error) {
            Connect.log(error);
        }
    };


    // TODO: Check if the same hook can be used for creating the destination
    const fetchAuthTokenWithDestinationWrapper = useCallback(async (destination: PreparedDestination) => {
        return await fetchTokenWithDestination(fetchAuthTokenUrl, destination);
    }, []);

    const testConnection = useTestConnection(
        fetchAuthTokenWithDestinationWrapper,
        window.location.host,
        PREQUEL_API_HOST
    );

    const createDestination = useCreateDestination(
        fetchAuthTokenWithDestinationWrapper,
        window.location.host,
        PREQUEL_API_HOST
    );

    async function updateDestination(destinationId: string, destinationPayload: PreparedDestination): Promise<any> {
        try {
            //The below values are not needed for the update destination API as thay are set during the creation of the destination
            const {
                type,
                recipient_id,
                vendor,
                name,
                id_in_provider_system,
                ...updatableFields
            } = destinationPayload;

            const csrf = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content');
            const options = {
                method: 'PATCH',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': csrf
                }),
                body: JSON.stringify({ destination: updatableFields }),
            };

            const response = await window.fetch(`${state.settingsUrl.replace('.json', `/zdq/update_destination/${destinationId}`)}`, options);
            const updateDestinationData = await response.json();

            if (updateDestinationData && updateDestinationData.status === 'success') {
                setToast({ ...toast, show: true, duration: 100000, severity: 'success', msg: 'Destination updated successfully', loading: false });
                return updateDestinationData;
            }
            if (!response.ok) {
                setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: `Error updating destination: ${destinationId}, message: ${updateDestinationData.message}, statusText: ${response.statusText}`, loading: false });
                throw Error(`Error updating destination: ${destinationId}, message: ${updateDestinationData.message}, statusText: ${response.statusText}`);
            }
            return updateDestinationData;
        } catch (error) {
            Connect.log(error);
            setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: 'Encountered an error while updating the destination', loading: false });
            return {
                status: 'error',
                message: 'An error occurred while updating the destination'
            };
        }
    }

    async function handleSaveAndInstall() {
        setConfirmationModalOpen(false);
        const actionText = isEditMode ? 'Updating' : 'Creating';
        setToast({ ...toast, duration: 100000, show: true, msg: `${actionText} Destination...`, severity: 'info', loading: true });
        try {
            const currentPreparedDestination = getPreparedDestinationWithFormValues();
            const response = isEditMode && existingDestinationData
                ? await updateDestination(
                    existingDestinationData.id,
                    { ...currentPreparedDestination, is_enabled: currentPreparedDestination.is_enabled }
                )
                : await createDestination({ ...currentPreparedDestination, is_enabled: false });

            if (response && response.status === 'success' && response?.data?.destination) {
                setMainModalOpen(false);
                !isEditMode && setToast({ ...toast, show: true, duration: 100000, severity: 'success', msg: `Destination created successfully`, loading: false });
                dispatch({ type: ZDQ_DESTINATION_DATA, payload: response.data.destination });
                sendSlackNotification({ status: "success", vendorTitle: VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor], message: `Destination ${actionText.toLowerCase()} successfully` });
                return;
            }
            setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: response?.message || `Encountered an error while trying to ${actionText.toLowerCase()} the destination. Please try again`, loading: false });
            sendSlackNotification({ error: true, vendorTitle: VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor], message: response?.message });
            Connect.log(`${actionText} Destination:`, response);
        } catch (error) {
            setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: `Encountered an error while ${actionText.toLowerCase()} the destination`, loading: false });
            Connect.log('Error in handleSaveAndInstall:', error);
            sendSlackNotification({ error: true, vendorTitle: VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor], message: error });
        }
    }

    const onSubmit: SubmitHandler<any> = (data) => {
        testDestinationConnection();
    };
    async function testDestinationConnection() {
        try {
            // Get the current prepared destination with form values
            const currentPreparedDestination = getPreparedDestinationWithFormValues() || preparedDestination;
            setToast({ ...toast, duration: 100000, show: true, msg: 'Testing Connection...', severity: 'info', loading: true });
            const res = await testConnection({ ...currentPreparedDestination, is_enabled: false });
            if (res?.status === 'success' && res.data?.status === 'success') {
                setToast({ ...toast, show: true, duration: 10000, severity: 'success', msg: 'Connection test passed. Destination is ready to be saved.', loading: false });
                setTestConnectionPassed(true);
                return;
            }
            setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: res?.message || "Unable to test connection", loading: false });
        } catch (e) {
            setToast({ ...toast, show: true, duration: 100000, severity: 'error', msg: "Encountered an error while testing destination", loading: false });
            Connect.log("Error while testing connection", e);
        }
    }

    return (
        <>
            <Box sx={{ marginTop: 2 }}>
                {toast.show && <ToastController closeable loading={toast.loading} duration={toast.duration} severity={toast.severity as any} message={toast.msg} />}
                <Alert
                    header="Test the connection before saving"
                    open
                    severity="info"
                >
                    <div>The save function will remain inactive until the connection test is completed and passed</div>
                </Alert>
            </Box>
            <Grid sx={{ marginTop: 5 }} container direction='row' justify="flex-end">
                <Grid item>
                    <Button
                        dsOnClick={handleSubmit(onSubmit)}
                        variant="outlined"
                        body="Test Connection"
                    />
                </Grid>
                <Grid item>
                    <Button
                        dsOnClick={handleSubmit(setConfirmationModalOpen)}
                        disabled={!testConnectionPassed}
                        body={isEditMode ? 'Save Destination' : 'Save and Install'}
                    />
                </Grid>
            </Grid>

            <Modal maxWidth="sm"
                id="confirmation-modal" open={confirmationModalOpen} dsOnClose={() => { setConfirmationModalOpen(false) }}
                header={
                    <ModalTitle icon={<Icon body='info' variant="filled" color="info" ></Icon >}
                        dsOnClose={() => { setConfirmationModalOpen(false) }} body={`${isEditMode ? 'Save and Update' : 'Save and Install'} your ${VENDOR_TO_CONNECTOR_TITLE_MAPPING[vendor]} Connection`} />
                }
                body={
                    <>
                        <Typography variant="body1"
                            body={
                                isEditMode ? (
                                    <>
                                        Clicking on <strong>Save Destination</strong> will update the connector configuration. The updated configuration will take effect in the next data sync cycle.
                                        <br />
                                        <br />
                                        ⚠️ <strong>Note:</strong> Please ensure the configuration details are correct before saving. Any incorrect settings may cause data sync failures.
                                        <br />
                                        <br />
                                        Kindly confirm if you would like to proceed.
                                        <br />
                                    </>
                                ) : (
                                    <>
                                        Clicking <strong>Save & Install</strong> will start the connector provisioning process. Once provisioning is complete, the connector status will display as <strong>Active</strong> in the Setup tab. Sync metrics will appear in the Status tab after the first successful data transfer, which may take time depending on the tenant’s configured sync frequency and historical data volume for the initial load.
                                        <br />
                                        <br />
                                        You can monitor the progress on the Authentication Setup page. When the status changes to Active, it means the connection has been successfully established.
                                        <br />
                                        <br />
                                        ⚠️ <strong>Note:</strong> This action cannot be undone via the UI. If you need to revert the change, please contact your Zuora representative.
                                        Kindly confirm if you would like to proceed.
                                        <br />
                                    </>
                                )
                            }>
                        </Typography >
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', margin: '2%' }}>
                            <Button
                                dsOnClick={() => {
                                    setConfirmationModalOpen(false)
                                }}
                                variant="outlined"
                                body="Cancel"
                            />
                            <Button sx={{ marginLeft: '2%' }}
                                dsOnClick={handleSaveAndInstall}
                                body={isEditMode ? 'Save Destination' : 'Save & Install'}
                            />
                        </Box>
                    </>
                }
            />
        </>
    )

}

export default TestConnection